import { useMemo } from "react";
import { Grid, Select } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  useSourcesWithSubsites,
  useCollectionMethods,
} from "../../hooks/useReferenceData";
import GrossTemplateSelector, {
  type GrossTemplateSelectorHandle,
} from "./GrossTemplateSelector";
import FragmentCountInput from "./FragmentCountInput";
import {
  getFragmentCountDisplayValue,
  type FragmentCountPatch,
  type FragmentCountQualifier,
} from "../../utils/fragmentCount";

export interface SpecimenGrossingFieldValues {
  gross?: string | null;
  internalGrossCodeId?: string | null;
  sourceId?: string | null;
  subsiteId?: string | null;
  collectionMethod?: string | null;
  fragmentCount?: number | null;
  fragmentCountQualifier?: FragmentCountQualifier | null;
  tissueDisposition?: string | null;
}

interface SpecimenGrossingFieldsProps {
  /** Current specimen data from the server */
  specimen: any;
  /** Local overrides (unsaved edits) */
  values: SpecimenGrossingFieldValues;
  /** Called when any field changes */
  onChange: (field: string, value: string | number | null) => void;
  /** Called when count and qualifier must change atomically. */
  onFragmentCountChange: (patch: FragmentCountPatch) => void;
  /** Whether the fields are disabled */
  disabled?: boolean;
  /** Field-level errors (keyed by field name) */
  fieldErrors?: Record<string, boolean>;
  /** Called when a field error is cleared */
  onClearError?: (field: string) => void;
  /** Total number of specimens (for template variables) */
  specimenCount?: number;
  /** Ref for the GrossTemplateSelector */
  grossTemplateSelectorRef?: React.RefObject<GrossTemplateSelectorHandle>;
  /** Called when Tab leaves the gross editor */
  onGrossTabOut?: () => void;
}

export default function SpecimenGrossingFields({
  specimen,
  values,
  onChange,
  onFragmentCountChange,
  disabled = false,
  fieldErrors = {},
  onClearError,
  specimenCount = 1,
  grossTemplateSelectorRef,
  onGrossTabOut,
}: SpecimenGrossingFieldsProps) {
  const { data: sourcesData } = useSourcesWithSubsites();
  const { data: collectionMethods = [] } = useCollectionMethods();

  const sources = sourcesData?.sources || [];
  const subsitesBySource = useMemo(
    () => sourcesData?.subsitesBySource || {},
    [sourcesData?.subsitesBySource]
  );

  // Compute current values (local overrides take precedence)
  const currentSourceId =
    values.sourceId !== undefined ? values.sourceId : specimen?.sourceId ?? "";
  const currentSubsiteId =
    values.subsiteId !== undefined
      ? values.subsiteId
      : specimen?.subsiteId ?? null;
  const currentCollectionMethod =
    values.collectionMethod !== undefined
      ? values.collectionMethod
      : specimen?.collectionMethod ?? "";
  const currentFragmentCount =
    values.fragmentCount !== undefined
      ? values.fragmentCount
      : specimen?.fragmentCount ?? undefined;
  const currentFragmentCountQualifier =
    values.fragmentCountQualifier !== undefined
      ? values.fragmentCountQualifier
      : specimen?.fragmentCountQualifier ?? null;
  const currentFragmentCountDisplay = useMemo(
    () =>
      getFragmentCountDisplayValue(
        currentFragmentCount,
        currentFragmentCountQualifier
      ),
    [currentFragmentCount, currentFragmentCountQualifier]
  );
  const fragmentCountDraftKey = useMemo(
    () => specimen?.id ?? "unknown",
    [specimen?.id]
  );
  const currentGross =
    values.gross !== undefined ? values.gross : specimen?.gross ?? "";
  const currentGrossCodeId =
    values.internalGrossCodeId !== undefined
      ? values.internalGrossCodeId
      : specimen?.internalGrossCodeId;

  // Get subsites for current source, include current subsite if it doesn't belong
  const availableSubsites = useMemo(() => {
    const baseSubsites = currentSourceId
      ? subsitesBySource[currentSourceId] || []
      : [];
    if (!currentSubsiteId) return baseSubsites;
    const currentInList = baseSubsites.some(
      (s: { value: string }) => s.value === currentSubsiteId
    );
    if (currentInList) return baseSubsites;
    const currentSubsiteName = specimen?.subsite?.name || "Unknown Subsite";
    return [
      {
        value: currentSubsiteId,
        label: `${currentSubsiteName} (from previous source)`,
      },
      ...baseSubsites,
    ];
  }, [
    currentSourceId,
    currentSubsiteId,
    subsitesBySource,
    specimen?.subsite?.name,
  ]);

  const handleSourceChange = (value: string | null) => {
    onChange("sourceId", value);
    onClearError?.("source");

    // If current subsite doesn't belong to the new source, clear it
    if (value) {
      const newSubsites = subsitesBySource[value] || [];
      const oldSubsiteId =
        values.subsiteId !== undefined ? values.subsiteId : specimen?.subsiteId;
      if (
        oldSubsiteId &&
        !newSubsites.some((s: { value: string }) => s.value === oldSubsiteId)
      ) {
        onChange("subsiteId", null);
        const newSource = sources.find(
          (s: { value: string; label: string }) => s.value === value
        );
        notifications.show({
          message: `Subsite cleared (not available for ${
            newSource?.label ?? "new source"
          })`,
          color: "blue",
          autoClose: 3000,
        });
      }
      // Auto-fill collection method from source default if empty
      const isCollectionMethodValid =
        currentCollectionMethod &&
        collectionMethods.some(
          (m: { value: string }) => m.value === currentCollectionMethod
        );
      if (!isCollectionMethodValid) {
        const selectedSource = sources.find(
          (s: { value: string }) => s.value === value
        );
        if (selectedSource?.defaultCollectionMethodName) {
          onChange(
            "collectionMethod",
            selectedSource.defaultCollectionMethodName
          );
          onClearError?.("collectionMethod");
        }
      }
    }
  };

  return (
    <>
      {/* Gross Template / Description Editor */}
      <GrossTemplateSelector
        key={specimen?.id}
        ref={grossTemplateSelectorRef}
        value={currentGross}
        onChange={(val) => {
          onChange("gross", val);
          onClearError?.("gross");
        }}
        grossCodeId={currentGrossCodeId}
        onGrossCodeChange={(id) => onChange("internalGrossCodeId", id)}
        error={fieldErrors.gross}
        specimenLetter={specimen?.number}
        specimenCount={specimenCount}
        sourceName={
          sources.find(
            (s: { value: string; label: string }) => s.value === currentSourceId
          )?.label
        }
        subsiteName={
          availableSubsites.find(
            (s: { value: string; label: string }) =>
              s.value === currentSubsiteId
          )?.label
        }
        collectionMethod={
          collectionMethods.find(
            (m: { value: string; label: string }) =>
              m.value === currentCollectionMethod
          )?.label
        }
        cassetteCount={specimen?.blocks?.length || 0}
        fragmentCount={currentFragmentCount}
        fragmentCountQualifier={currentFragmentCountQualifier}
        blockCount={specimen?.blocks?.length || 0}
        onFragmentCountChange={onFragmentCountChange}
        onTabOut={onGrossTabOut}
      />

      {/* Source / Subsite / Collection Method / Fragment Count */}
      <Grid>
        <Grid.Col span={{ base: 6, sm: 6, md: 3 }} style={{ minWidth: 200 }}>
          <Select
            label="Source"
            placeholder="Select source"
            data={sources}
            value={currentSourceId}
            onChange={handleSourceChange}
            searchable
            required
            error={fieldErrors.source}
            disabled={disabled}
            tabIndex={-1}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 6, sm: 6, md: 3 }} style={{ minWidth: 200 }}>
          <Select
            label="Subsite"
            placeholder="Select subsite"
            data={availableSubsites}
            value={currentSubsiteId || null}
            onChange={(value) => onChange("subsiteId", value)}
            searchable
            clearable
            disabled={
              disabled || (availableSubsites.length === 0 && !currentSubsiteId)
            }
            tabIndex={-1}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 6, sm: 6, md: 3 }} style={{ minWidth: 200 }}>
          <Select
            label="Collection Method"
            placeholder="Select method"
            data={collectionMethods}
            value={currentCollectionMethod}
            onChange={(value) => {
              onChange("collectionMethod", value);
              onClearError?.("collectionMethod");
            }}
            searchable
            required
            error={fieldErrors.collectionMethod}
            disabled={disabled}
            tabIndex={-1}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 6, sm: 6, md: 3 }} style={{ minWidth: 200 }}>
          <FragmentCountInput
            key={fragmentCountDraftKey}
            canonicalValue={currentFragmentCountDisplay}
            disabled={disabled}
            onFragmentCountChange={onFragmentCountChange}
          />
        </Grid.Col>
      </Grid>
    </>
  );
}
